import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { KeenIcon, DataGrid } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import { clubResourceValidationSchema } from '@/utils/validations';
import {
  useGetClubResourcesQuery,
  useDeleteClubResourceMutation,
  useCreateClubResourceMutation,
  useUpdateClubResourceMutation,
} from '@/services/ClubResourceService';
import { ErrorApiResponse } from '@/types/api.types';
import {
  IClubResourceData,
  ResourceType,
  RecurrencePattern,
  PriceUnit,
} from '@/types/club-resource.types';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { ClubResourceFormModal } from './ClubResourceFormModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatFinnishCurrency, formatFinnishNumber } from '@/utils';

interface ClubResourcesListingProps {
  clubId?: string;
}

const ClubResourcesListing = ({ clubId }: ClubResourcesListingProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    searchTerm,
    handleSearch,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    resource: IClubResourceData | null;
    mode: 'view' | 'edit' | 'create';
  }>({ isOpen: false, resource: null, mode: 'view' });

  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    resourceId: string | null | undefined;
  }>({ isOpen: false, resourceId: null });

  const [formData, setFormData] = useState<Partial<IClubResourceData>>({
    name: '',
    description: '',
    club_id: clubId,
    resource_type: undefined as ResourceType | undefined,
    allow_booking: false,
    slot_length: '',
    quantity: undefined as number | undefined,
    recurrence_pattern: undefined as RecurrencePattern | undefined,
    max_bookings_period_days: undefined as number | undefined,
    define_price: false,
    price: undefined as number | undefined,
    price_unit: undefined as PriceUnit | undefined,
    start_date: '',
    end_date: '',
    shift_start_date: '',
    shift_end_date: '',
    shift_start_time: '',
    shift_end_time: '',
    seasonal_dates: {
      spring: { start_date: '', end_date: '' },
      summer: { start_date: '', end_date: '' },
      autumn: { start_date: '', end_date: '' },
      winter: { start_date: '', end_date: '' },
    },
    status: 1,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const { hasPermission } = usePermissions();

  const {
    data: resourcesResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetClubResourcesQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue || undefined,
    club_id: clubId,
  });

  const [createResource, { isLoading: isCreatingResource }] =
    useCreateClubResourceMutation();
  const [updateResource] = useUpdateClubResourceMutation();
  const [deleteResource] = useDeleteClubResourceMutation();

  useEffect(() => {
    if (resourcesResponse) {
      handleTotalPages(resourcesResponse.totalPages || 1);
      handleTotalItems(resourcesResponse.total || 0);
    }
  }, [resourcesResponse, handleTotalPages, handleTotalItems]);

  const handleOpenModal = (
    resource: IClubResourceData | null,
    mode: 'view' | 'edit' | 'create'
  ) => {
    if (mode === 'create') {
      setModalState({ isOpen: true, resource: null, mode: 'create' });
      setFormData({
        name: '',
        description: '',
        club_id: clubId,
        resource_type: undefined,
        allow_booking: false,
        slot_length: '',
        quantity: undefined,
        recurrence_pattern: undefined,
        max_bookings_period_days: undefined,
        define_price: false,
        price: undefined,
        price_unit: undefined,
        shift_start_date: '',
        shift_end_date: '',
        shift_start_time: '',
        shift_end_time: '',
        seasonal_dates: {
          spring: { start_date: '', end_date: '' },
          summer: { start_date: '', end_date: '' },
          autumn: { start_date: '', end_date: '' },
          winter: { start_date: '', end_date: '' },
        },
        status: 1,
      });
      setErrors({});
    } else if (resource?.id && (mode === 'view' || mode === 'edit')) {
      setModalState({ isOpen: true, resource, mode });
      setFormData({
        name: resource.name,
        description: resource.description,
        club_id: resource.club_id || clubId,
        resource_type: resource.resource_type,
        allow_booking: resource.allow_booking,
        slot_length: resource.slot_length,
        quantity: resource.quantity,
        recurrence_pattern: resource.recurrence_pattern,
        max_bookings_period_days: resource.max_bookings_period_days,
        define_price: resource.define_price,
        price: resource.price,
        price_unit: resource.price_unit,
        start_date: resource.start_date || '',
        end_date: resource.end_date || '',
        shift_start_date: resource.shift_start_date || '',
        shift_end_date: resource.shift_end_date || '',
        shift_start_time: resource.shift_start_time || '',
        shift_end_time: resource.shift_end_time || '',
        seasonal_dates: resource.seasonal_dates || {
          spring: { start_date: '', end_date: '' },
          summer: { start_date: '', end_date: '' },
          autumn: { start_date: '', end_date: '' },
          winter: { start_date: '', end_date: '' },
        },
        status: resource.status,
      });
      setErrors({});
    }
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, resource: null, mode: 'view' });
    setFormData({
      name: '',
      description: '',
      club_id: clubId,
      resource_type: undefined,
      allow_booking: false,
      slot_length: '',
      quantity: undefined,
      recurrence_pattern: undefined,
      max_bookings_period_days: undefined,
      define_price: false,
      price: undefined,
      price_unit: undefined,
      shift_start_date: '',
      shift_end_date: '',
      shift_start_time: '',
      shift_end_time: '',
      seasonal_dates: {
        spring: { start_date: '', end_date: '' },
        summer: { start_date: '', end_date: '' },
        autumn: { start_date: '', end_date: '' },
        winter: { start_date: '', end_date: '' },
      },
      status: 1,
    });
    setErrors({});
  };

  const validateField = useCallback(
    async (name: string, value: unknown) => {
      try {
        await clubResourceValidationSchema.validateAt(
          name,
          { ...formData, [name]: value },
          { abortEarly: true }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({
            ...prev,
            [name]: err.errors[0] || 'Invalid input',
          }));
        }
      }
    },
    [formData]
  );

  const handleSubmit = async (formData: Partial<IClubResourceData>) => {
    try {
      await clubResourceValidationSchema.validate(formData, {
        abortEarly: false,
      });

      const payload: Omit<
        IClubResourceData,
        'id' | 'created_at' | 'updated_at'
      > = {
        name: formData.name || '',
        description: formData.description || '',
        resource_type: formData.resource_type,
        club_id: formData.club_id || clubId,
        allow_booking: formData.allow_booking,
        slot_length: formData.slot_length,
        quantity: formData.quantity,
        recurrence_pattern: formData.recurrence_pattern,
        max_bookings_period_days: formData.max_bookings_period_days,
        define_price: formData.define_price,
        seasonal_dates: formData.seasonal_dates,
        status: formData.status || 1,
        translations: formData.translations,
      };

      // Only include price and price_unit if define_price is true
      if (formData.define_price) {
        payload.price = formData.price;
        payload.price_unit = formData.price_unit;
      }

      // Include PORT_WATCH_SHIFTS specific fields if resource_type is 4
      if (formData.resource_type === 4) {
        payload.shift_start_date = formData.shift_start_date;
        payload.shift_end_date = formData.shift_end_date;
        payload.shift_start_time = formData.shift_start_time;
        payload.shift_end_time = formData.shift_end_time;
        payload.port_watch_duty_liability =
          formData.port_watch_duty_liability as number | undefined;
        payload.port_watch_buyout_amount = formData.port_watch_buyout_amount as
          number | undefined;
      }

      // For Sauna Booking, include sauna-specific fields
      if (formData.resource_type === 1) {
        // SAUNA_BOOKING
        payload.weekly_windows = formData.weekly_windows || {};
        payload.special_date_windows = formData.special_date_windows || [];
        payload.closed_dates = formData.closed_dates || [];
      }

      if (modalState.mode === 'create') {
        await createResource(payload).unwrap();
      } else if (modalState.mode === 'edit' && modalState.resource?.id) {
        await updateResource({
          id: modalState.resource.id,
          data: payload,
        }).unwrap();
      }

      enqueueSnackbar(
        modalState.mode === 'create'
          ? 'Resource created successfully'
          : 'Resource updated successfully',
        { variant: 'success' }
      );
      handleCloseModal();
      refetch();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        console.error('Validation failed', validationErrors);
        enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      } else {
        const errorMessage =
          (err as ErrorApiResponse)?.data?.message ||
          'Failed to process resource';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteState.resourceId) return;
    try {
      await deleteResource(confirmDeleteState.resourceId).unwrap();
      enqueueSnackbar('Resource deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, resourceId: null });
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteState({ isOpen: false, resourceId: null });
    }
  };

  const handleViewResource = useCallback(
    (resource: IClubResourceData) => {
      // Check if this is an Annual Inspection resource
      if (resource.resource_type === ResourceType.ANNUAL_INSPECTIONS) {
        if (!clubId) {
          enqueueSnackbar('Club ID is required', { variant: 'error' });
          return;
        }
        navigate(
          `/club-management/${clubId}/annual-inspections/${resource.id}?mode=view`,
          {
            state: {
              from: 'club-resources',
              returnTo: `${location.pathname}${location.hash}`,
            },
          }
        );
      } else {
        // Open regular resource modal in view mode
        setModalState({
          isOpen: true,
          resource,
          mode: 'view',
        });
        setFormData({
          name: resource.name,
          description: resource.description,
          club_id: resource.club_id || clubId,
          resource_type: resource.resource_type,
          allow_booking: resource.allow_booking,
          slot_length: resource.slot_length,
          quantity: resource.quantity,
          recurrence_pattern: resource.recurrence_pattern,
          max_bookings_period_days: resource.max_bookings_period_days,
          define_price: resource.define_price,
          price: resource.price,
          price_unit: resource.price_unit,
          start_date: resource.start_date || '',
          end_date: resource.end_date || '',
          shift_start_date: resource.shift_start_date || '',
          shift_end_date: resource.shift_end_date || '',
          shift_start_time: resource.shift_start_time || '',
          shift_end_time: resource.shift_end_time || '',
          seasonal_dates: resource.seasonal_dates || {
            spring: { start_date: '', end_date: '' },
            summer: { start_date: '', end_date: '' },
            autumn: { start_date: '', end_date: '' },
            winter: { start_date: '', end_date: '' },
          },
          status: resource.status,
        });
        setErrors({});
      }
    },
    [clubId, enqueueSnackbar, location.hash, location.pathname, navigate]
  );

  const handleEditResource = useCallback(
    (resource: IClubResourceData) => {
      // Check if this is an Annual Inspection resource
      if (resource.resource_type === ResourceType.ANNUAL_INSPECTIONS) {
        if (!clubId) {
          enqueueSnackbar('Club ID is required', { variant: 'error' });
          return;
        }
        navigate(
          `/club-management/${clubId}/annual-inspections/${resource.id}?mode=edit`,
          {
            state: {
              from: 'club-resources',
              returnTo: `${location.pathname}${location.hash}`,
            },
          }
        );
      } else {
        // Open regular resource modal in edit mode
        setModalState({
          isOpen: true,
          resource,
          mode: 'edit',
        });
        setFormData({
          name: resource.name,
          description: resource.description,
          club_id: resource.club_id || clubId,
          resource_type: resource.resource_type,
          allow_booking: resource.allow_booking,
          slot_length: resource.slot_length,
          quantity: resource.quantity,
          recurrence_pattern: resource.recurrence_pattern,
          max_bookings_period_days: resource.max_bookings_period_days,
          define_price: resource.define_price,
          price: resource.price,
          price_unit: resource.price_unit,
          start_date: resource.start_date || '',
          end_date: resource.end_date || '',
          shift_start_date: resource.shift_start_date || '',
          shift_end_date: resource.shift_end_date || '',
          shift_start_time: resource.shift_start_time || '',
          shift_end_time: resource.shift_end_time || '',
          seasonal_dates: resource.seasonal_dates || {
            spring: { start_date: '', end_date: '' },
            summer: { start_date: '', end_date: '' },
            autumn: { start_date: '', end_date: '' },
            winter: { start_date: '', end_date: '' },
          },
          status: resource.status,
        });
        setErrors({});
      }
    },
    [clubId, enqueueSnackbar, location.hash, location.pathname, navigate]
  );

  const handleViewShifts = useCallback(
    (resourceId: string) => {
      navigate(`/club-resources/${resourceId}/shifts`);
    },
    [navigate]
  );

  const handleOpenAnnualInspectionPage = useCallback(() => {
    if (!clubId) {
      enqueueSnackbar('Club ID is required', { variant: 'error' });
      return;
    }
    navigate(`/club-management/${clubId}/annual-inspections/create`, {
      state: {
        from: 'club-resources',
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  }, [clubId, enqueueSnackbar, location.pathname, location.search, navigate]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Resource Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) =>
          hasPermission('club_resource_management_read') ? (
            <button
              onClick={() => handleViewResource(row.original)}
              className="text-sm font-medium text-gray-900 hover:text-primary-active text-start focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`View ${row.original.name}`}
            >
              {row.original.name}
            </button>
          ) : (
            <span className="text-sm font-medium text-gray-900">
              {row.original.name}
            </span>
          ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.description || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'slot_length',
        header: 'Slot Length',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.slot_length || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'recurrence_pattern',
        header: 'Recurrence',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <span className="text-gray-800 font-normal capitalize">
            {row.original.recurrence_pattern || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'max_bookings_period_days',
        header: 'Max Booking Days',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.max_bookings_period_days !== undefined
              ? `${formatFinnishNumber(row.original.max_bookings_period_days, {
                  maximumFractionDigits: 0,
                })} days`
              : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'allow_booking',
        header: 'Allow Booking',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <div className="flex items-center">
            {row.original.allow_booking ? (
              <>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-success/10">
                  <KeenIcon
                    type="solid"
                    icon="check-circle"
                    className="text-green-700 dark:text-green-600 contrast:text-green-600 text-base"
                  />
                </div>
                <span className="text-sm font-medium text-green-700 dark:text-green-600 contrast:text-green-600">
                  Enabled
                </span>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
                  <KeenIcon
                    type="solid"
                    icon="cross-circle"
                    className="text-gray-400 text-base"
                  />
                </div>
                <span className="text-sm font-medium text-gray-500">
                  Disabled
                </span>
              </>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'price',
        header: 'Price',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.price !== undefined && row.original.price !== null
              ? formatFinnishCurrency(row.original.price)
              : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'price_unit',
        header: 'Price Unit',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.price_unit
              ? row.original.price_unit.replace('PER_', 'Per ')
              : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <span
            className={`badge ${
              row.original.status === 1 ? 'badge-success' : 'badge-danger'
            }`}
          >
            {row.original.status === 1 ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubResourceData } }) => (
          <div className="flex items-center gap-2">
            {hasPermission('club_resource_management_read') && (
              <button
                type="button"
                onClick={() => handleViewResource(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="View"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {hasPermission('club_resource_management_read') &&
              row.original.resource_type === 4 && (
                <button
                  type="button"
                  onClick={() => handleViewShifts(row.original.id!)}
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="View Shifts"
                  aria-label={`View shifts for ${row.original.name}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="calendar"
                    className="text-blue-500"
                  />
                </button>
              )}
            {hasPermission('club_resource_management_update') && (
              <button
                type="button"
                onClick={() => handleEditResource(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Edit"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {hasPermission('club_resource_management_delete') && (
              <button
                type="button"
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    resourceId: row.original.id,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Delete"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [handleViewResource, handleEditResource, handleViewShifts, hasPermission]
  );

  const filteredData: IClubResourceData[] = resourcesResponse?.data || [];

  return (
    <div className="w-full">
      <div className="card card-grid min-w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-resource"
                aria-label="Search resources"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                />
                <input
                  id="search-resource"
                  placeholder="Search resources"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e)}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search resources by name"
                />
              </label>
            </div>
            {searchTerm && (
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={resetSearch}
                aria-label="Clear search filter"
                title="Clear search filter"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filter
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasPermission('club_resource_management_create') && (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => handleOpenModal(null, 'create')}
                aria-label="Add new resource"
              >
                Add New Resource
              </button>
            )}

            {hasPermission(
              'club_resource_management_create_inspection_slot'
            ) && (
              <button
                className="btn btn-sm btn-secondary flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleOpenAnnualInspectionPage}
                aria-label="Add Annual Inspection"
                title="Add Annual Inspection"
              >
                <KeenIcon
                  type="solid"
                  icon="calendar-tick"
                  className="text-white"
                />
                Add Annual Inspection
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          {isLoading || isFetching ? (
            <div className="text-center py-4" role="status" aria-live="polite">
              Loading resources...
            </div>
          ) : isError ? (
            <div className="text-center py-4 text-red-500" role="alert">
              Failed to load resources. Please try again.
            </div>
          ) : (
            <DataGrid
              columns={columns}
              data={filteredData}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'name', desc: false }]}
              serverSide={true}
              onFilterReset={resetSearch}
              hasActiveFilters={!!searchTerm}
              aria-label="Club resources table"
            />
          )}
          <ClubResourceFormModal
            open={modalState.isOpen}
            onClose={handleCloseModal}
            mode={modalState.mode}
            resourceId={modalState.resource?.id}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            onSubmit={handleSubmit}
            isSubmitting={isCreatingResource}
            validateField={validateField}
          />
          <ConfirmationDialog
            open={confirmDeleteState.isOpen}
            onClose={() =>
              setConfirmDeleteState({ isOpen: false, resourceId: null })
            }
            onConfirm={handleDelete}
            message="Are you sure you want to delete this resource?"
            cancelButtonText="No"
            confirmButtonText="Yes"
            aria-labelledby="delete-confirmation-title"
            aria-describedby="delete-confirmation-description"
          />
        </div>
      </div>
    </div>
  );
};

export { ClubResourcesListing };
